import { createRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { Route as TenantLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useTenantHolders, type TenantHolder } from "../../hooks/useTenantCards";

/**
 * Registr držitelů střediska (tenantAdmin, read-only). Zakládání/úpravy držitelů
 * dělá globalAdmin; tenantAdmin je vidí a přiřazuje kartám (viz „Všechny karty").
 */

function TenantHoldersPage() {
  const { data, isLoading, error } = useTenantHolders();
  const rows = data?.rows ?? [];

  const columns = useMemo<Column<TenantHolder>[]>(
    () => [
      { key: "name", header: "Název", cell: (r) => <strong className="text-primary">{r.display_name}</strong> },
      {
        key: "type",
        header: "Typ",
        cell: (r) => (
          <Badge color={r.holder_type === "legal" ? "blue" : "gray"}>
            {r.holder_type === "legal" ? "Právnická osoba" : "Fyzická osoba"}
          </Badge>
        ),
      },
      {
        key: "detail",
        header: "Identifikace",
        cell: (r) =>
          r.holder_type === "legal" ? (
            <span className="text-sm text-tertiary">{r.ico ? `IČO ${r.ico}` : "—"}</span>
          ) : (
            <span className="text-sm text-tertiary">app uživatel</span>
          ),
      },
    ],
    [],
  );

  return (
    <section aria-labelledby="tenant-holders-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="tenant-holders-title" className="text-2xl font-bold text-primary m-0">
          Držitelé
        </h2>
        <p className="text-base text-tertiary m-0">
          Registr držitelů střediska — právnické (firmy) a fyzické osoby.
        </p>
      </header>

      {error && (
        <InlineAlert variant="error">
          {error instanceof Error ? error.message : "Načtení držitelů selhalo."}
        </InlineAlert>
      )}

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-primary m-0">
            Seznam držitelů
            {!isLoading && <span className="ml-2 text-sm font-normal text-tertiary">({rows.length})</span>}
          </h3>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <LoadingIndicator label="Načítání…" />
          ) : (
            <DataTable
              caption="Registr držitelů střediska"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              emptyMessage="Žádní držitelé."
            />
          )}
        </CardBody>
      </Card>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/holders",
  component: TenantHoldersPage,
});
